import { useEffect, useState } from "react";
import { 
  Save, Plus, X, Layout, Users, Check
} from "lucide-react";
import axios from "axios";
import { API, getAuthHeader } from "./utils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

// Import extracted components
import SponsorListEditor from "./homepage/SponsorListEditor";
import SortableSectionItem from "./homepage/SortableSectionItem";
import RecentVideosEditor from "./homepage/RecentVideosEditor";
import DynamicFieldRenderer, { HeroImageEditor } from "./homepage/DynamicFieldRenderer";
import { BUILTIN_SECTIONS, BUILTIN_SECTION_CONFIG, CUSTOM_TEMPLATES } from "./homepage/constants";

// ============================================
// MAIN HOMEPAGE TAB COMPONENT
// ============================================
export const HomepageTab = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [recordings, setRecordings] = useState([]); // On-demand recordings for video selector

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch section order, custom section content, and recordings
      const [sectionsRes, homepageRes, recordingsRes] = await Promise.all([
        axios.get(`${API}/settings/homepage-sections`).catch(() => ({ data: { sections: [] } })),
        axios.get(`${API}/settings/homepage`).catch(() => ({ data: { custom_sections: [], recent_videos: [], sponsors_data: null, hero_data: null } })),
        axios.get(`${API}/recordings`).catch(() => ({ data: [] }))
      ]);

      const savedOrder = sectionsRes.data?.sections || [];
      const customSections = homepageRes.data?.custom_sections || [];
      const savedRecentVideos = homepageRes.data?.recent_videos || [];
      const savedVideosTitle = homepageRes.data?.videos_title || "";
      const savedSponsorsData = homepageRes.data?.sponsors_data || { title: "Our Sponsors", sponsors: [] };
      const savedHeroData = homepageRes.data?.hero_data || { 
        layout: "standard", 
        background_image: "",
        image_position_x: 50,
        image_position_y: 50,
        image_scale: 100,
        image_blur: 0,
        overlay_opacity: 70
      };
      
      // Store recordings for video selectors
      setRecordings(recordingsRes.data || []);

      // Build unified sections list
      let unifiedSections = [];

      if (savedOrder.length > 0) {
        // Use saved order, merging with custom section data
        unifiedSections = savedOrder.map(s => {
          // Check if it's a built-in section
          const builtin = BUILTIN_SECTIONS.find(b => b.id === s.id);
          if (builtin) {
            // For videos section, include the recent_videos data
            if (s.id === 'videos') {
              return { ...builtin, enabled: s.enabled !== false, recent_videos: savedRecentVideos, videos_title: savedVideosTitle };
            }
            // For sponsors section, include the sponsors data
            if (s.id === 'sponsors') {
              return { ...builtin, enabled: s.enabled !== false, data: savedSponsorsData };
            }
            // For hero section, include the hero data
            if (s.id === 'hero') {
              return { ...builtin, enabled: s.enabled !== false, data: savedHeroData };
            }
            return { ...builtin, enabled: s.enabled !== false };
          }
          
          // It's a custom section - find its data
          const customData = customSections.find(c => c.id === s.id);
          if (customData) {
            const template = CUSTOM_TEMPLATES.find(t => t.id === customData.template_id);
            return {
              ...customData,
              icon: template?.icon || Layout,
              template_name: template?.name,
              enabled: s.enabled !== false
            };
          }
          
          // Unknown section, skip
          return null;
        }).filter(Boolean);

        // Add any custom sections not in the order
        customSections.forEach(cs => {
          if (!unifiedSections.find(s => s.id === cs.id)) {
            const template = CUSTOM_TEMPLATES.find(t => t.id === cs.template_id);
            unifiedSections.push({
              ...cs,
              icon: template?.icon || Layout,
              template_name: template?.name,
              enabled: cs.enabled !== false
            });
          }
        });

        // Add any new built-in sections not yet in the saved order (disabled by default)
        BUILTIN_SECTIONS.forEach(b => {
          if (!unifiedSections.find(s => s.id === b.id)) {
            unifiedSections.push({ ...b, enabled: false });
          }
        });
      } else {
        // No saved order - use defaults + any custom sections
        unifiedSections = BUILTIN_SECTIONS.map(b => {
          if (b.id === 'sponsors') {
            return { ...b, enabled: true, data: savedSponsorsData };
          }
          if (b.id === 'hero') {
            return { ...b, enabled: true, data: savedHeroData };
          }
          return { ...b, enabled: true };
        });
        customSections.forEach(cs => {
          const template = CUSTOM_TEMPLATES.find(t => t.id === cs.template_id);
          unifiedSections.push({
            ...cs,
            icon: template?.icon || Layout,
            template_name: template?.name,
            enabled: cs.enabled !== false
          });
        });
      }

      setSections(unifiedSections);
    } catch (err) {
      console.error("Error fetching data:", err);
      // Fallback to defaults
      setSections(BUILTIN_SECTIONS.map(b => ({ ...b, enabled: true })));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Separate built-in order from custom section content
      const sectionOrder = sections.map(s => ({
        id: s.id,
        name: s.name,
        enabled: s.enabled
      }));

      const customSections = sections
        .filter(s => !s.builtin)
        .map(s => ({
          id: s.id,
          template_id: s.template_id,
          name: s.name,
          enabled: s.enabled,
          data: s.data || {}
        }));

      // Get recent_videos from the videos built-in section
      const videosSection = sections.find(s => s.id === 'videos');
      const recentVideos = videosSection?.recent_videos || [];
      const videosTitle = videosSection?.videos_title || "";
      
      // Get sponsors data from the sponsors built-in section
      const sponsorsSection = sections.find(s => s.id === 'sponsors');
      const sponsorsData = sponsorsSection?.data || { title: "Our Sponsors", sponsors: [] };
      
      // Get hero data from the hero built-in section
      const heroSection = sections.find(s => s.id === 'hero');
      const heroData = heroSection?.data || { 
        layout: "standard", 
        background_image: "",
        image_position_x: 50,
        image_position_y: 50,
        image_scale: 100,
        image_blur: 0,
        overlay_opacity: 70
      };

      // Save all settings
      await Promise.all([
        axios.put(`${API}/settings/homepage-sections`, { sections: sectionOrder }, { headers: getAuthHeader() }),
        axios.put(`${API}/settings/homepage`, { 
          custom_sections: customSections,
          recent_videos: recentVideos,
          videos_title: videosTitle,
          sponsors_data: sponsorsData,
          hero_data: heroData
        }, { headers: getAuthHeader() })
      ]);

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('homepageUpdated'));
    } catch (err) {
      console.error("Error saving:", err);
      alert("Error saving homepage settings");
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = sections.findIndex(s => s.id === active.id);
      const newIndex = sections.findIndex(s => s.id === over.id);
      setSections(arrayMove(sections, oldIndex, newIndex));
    }
  };

  const handleToggle = (sectionId) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const handleDelete = (sectionId, isBuiltin) => {
    const message = isBuiltin 
      ? "Delete this built-in section? You can add it back later from the 'Add Section' menu."
      : "Delete this custom section?";
    if (!window.confirm(message)) return;
    setSections(sections.filter(s => s.id !== sectionId));
  };

  const addBuiltinSection = (builtinSection) => {
    // Check if section already exists
    if (sections.find(s => s.id === builtinSection.id)) {
      alert("This section is already on the page");
      return;
    }
    setSections([...sections, { ...builtinSection, enabled: true }]);
    setShowTemplates(false);
  };

  const toggleExpand = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const updateSectionData = (sectionId, dataKey, value) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, data: { ...s.data, [dataKey]: value } } : s
    ));
  };

  const addCustomSection = (template) => {
    const uniqueId = `custom_${template.id}_${Date.now().toString(36)}`;
    const newSection = {
      id: uniqueId,
      template_id: template.id,
      name: template.name,
      icon: template.icon,
      template_name: template.name,
      enabled: true,
      builtin: false,
      data: { ...template.defaultData }
    };
    setSections([...sections, newSection]);
    setShowTemplates(false);
    setExpandedSections(prev => ({ ...prev, [uniqueId]: true }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Layout className="w-6 h-6 text-orange-500" />
            Homepage Sections
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Drag to reorder sections. Click the eye to show/hide. Add custom sections below.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowTemplates(true)}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Section
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-black font-bold rounded transition-colors disabled:opacity-50"
            data-testid="save-homepage-btn"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : saved ? (
              <Check className="w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saved ? "Saved!" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Section List */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sections.map(s => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {sections.map((section) => {
              const template = CUSTOM_TEMPLATES.find(t => t.id === section.template_id);
              
              return (
                <SortableSectionItem
                  key={section.id}
                  section={section}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                  isExpanded={expandedSections[section.id]}
                  onToggleExpand={toggleExpand}
                >
                  {/* Content Editor for Custom Sections */}
                  {template && (
                    <div className="space-y-4">
                      {/* Section Name */}
                      <div>
                        <label className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                          Section Name
                        </label>
                        <input
                          type="text"
                          value={section.name || ""}
                          onChange={(e) => setSections(sections.map(s => 
                            s.id === section.id ? { ...s, name: e.target.value } : s
                          ))}
                          className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
                        />
                      </div>
                      
                      {/* Template Fields */}
                      {template.fields.map(field => (
                        <div key={field.key}>
                          <label className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                            {field.label}
                          </label>
                          <DynamicFieldRenderer
                            section={section}
                            field={field}
                            updateSectionData={updateSectionData}
                            recordings={recordings}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Content Editor for Videos Built-in Section */}
                  {section.builtin && section.id === 'videos' && (
                    <RecentVideosEditor 
                      videos={section.recent_videos || []}
                      sectionTitle={section.videos_title || ""}
                      onChange={(videos) => {
                        setSections(sections.map(s => 
                          s.id === section.id ? { ...s, recent_videos: videos } : s
                        ));
                      }}
                      onTitleChange={(title) => {
                        setSections(sections.map(s => 
                          s.id === section.id ? { ...s, videos_title: title } : s
                        ));
                      }}
                    />
                  )}
                  
                  {/* Content Editor for Sponsors Built-in Section */}
                  {section.builtin && section.id === 'sponsors' && (
                    <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                      <h4 className="text-sm font-bold text-orange-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Sponsor Configuration
                      </h4>
                      
                      {/* Section Title */}
                      <div className="mb-4">
                        <label className="block text-xs text-zinc-500 uppercase mb-1">Section Title</label>
                        <input
                          type="text"
                          value={section.data?.title || ""}
                          onChange={(e) => {
                            setSections(sections.map(s => 
                              s.id === section.id 
                                ? { ...s, data: { ...s.data, title: e.target.value } } 
                                : s
                            ));
                          }}
                          placeholder="e.g., Our Sponsors, Partners, Supported By"
                          className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
                        />
                      </div>
                      
                      {/* Sponsors List - Using SponsorListEditor Component */}
                      <SponsorListEditor
                        sponsors={section.data?.sponsors || []}
                        onUpdate={(newSponsors) => {
                          setSections(sections.map(s => 
                            s.id === section.id 
                              ? { ...s, data: { ...s.data, sponsors: newSponsors } } 
                              : s
                          ));
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Content Editor for Hero Built-in Section */}
                  {section.builtin && section.id === 'hero' && expandedSections[section.id] && (
                    <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                      <h4 className="text-sm font-bold text-orange-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <Layout className="w-4 h-4" />
                        Hero Section Configuration
                      </h4>
                      
                      {/* Layout Style */}
                      <div className="mb-4">
                        <label className="block text-xs text-zinc-500 uppercase mb-1">Layout Style</label>
                        <select
                          value={section.data?.layout || "standard"}
                          onChange={(e) => {
                            setSections(sections.map(s => 
                              s.id === section.id 
                                ? { ...s, data: { ...s.data, layout: e.target.value } } 
                                : s
                            ));
                          }}
                          className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
                        >
                          <option value="standard">Standard (Full Height)</option>
                          <option value="compact">Compact (Shorter)</option>
                        </select>
                        <p className="text-xs text-zinc-600 mt-1">Compact layout reduces the hero section height</p>
                      </div>
                      
                      {/* Background Image Editor */}
                      <div className="mb-4">
                        <label className="block text-xs text-zinc-500 uppercase mb-2">Background Image</label>
                        <HeroImageEditor section={section} updateSectionData={updateSectionData} />
                      </div>
                    </div>
                  )}
                </SortableSectionItem>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      {/* Empty State */}
      {sections.length === 0 && (
        <div className="text-center py-12 bg-zinc-900 rounded-lg border border-zinc-800">
          <Layout className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">No sections configured</p>
          <button
            onClick={() => setShowTemplates(true)}
            className="mt-4 text-orange-500 hover:text-orange-400 font-medium"
          >
            Add your first section
          </button>
        </div>
      )}

      {/* Add Section Modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Add Section</h3>
              <button onClick={() => setShowTemplates(false)} className="text-zinc-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-6">
              {/* Built-in Sections (not already on page) */}
              {(() => {
                const missingBuiltins = BUILTIN_SECTIONS.filter(b => !sections.find(s => s.id === b.id));
                if (missingBuiltins.length === 0) return null;
                return (
                  <div>
                    <h4 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">Built-in Sections</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {missingBuiltins.map(section => {
                        const Icon = section.icon;
                        return (
                          <button
                            key={section.id}
                            onClick={() => addBuiltinSection(section)}
                            className="flex items-start gap-3 p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-left transition-colors group border border-zinc-700"
                          >
                            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center group-hover:bg-blue-500/20">
                              <Icon className="w-5 h-5 text-blue-500" />
                            </div>
                            <div>
                              <div className="text-white font-bold">{section.name}</div>
                              <div className="text-zinc-400 text-sm">Built-in section</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Custom Section Templates */}
              <div>
                <h4 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">Custom Sections</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {CUSTOM_TEMPLATES.map(template => {
                    const Icon = template.icon;
                    return (
                      <button
                        key={template.id}
                        onClick={() => addCustomSection(template)}
                        className="flex items-start gap-3 p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-left transition-colors group"
                      >
                        <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center group-hover:bg-orange-500/20">
                          <Icon className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <div className="text-white font-bold">{template.name}</div>
                          <div className="text-zinc-400 text-sm">{template.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomepageTab;
